import { type ReactNode, useEffect, useRef, useState } from "react";
import { submitFeedback, getClient, type FeedbackContext } from "@music-app/sync-supabase";
import { useCardsStore, usePrefsStore } from "@music-app/persistence";
import { Button } from "./Button.js";

interface FeedbackModalProps {
  screen: string;
  onClose: () => void;
  activeSkill?: string;
  sessionAccuracy?: number;
}

export function FeedbackModal({ screen, onClose, activeSkill, sessionAccuracy }: FeedbackModalProps): ReactNode {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [loves, setLoves] = useState("");
  const [hates, setHates] = useState("");
  const [wishes, setWishes] = useState("");
  const [wantsFollowup, setWantsFollowup] = useState(false);
  const [accountAgeDays, setAccountAgeDays] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");

  useEffect(() => {
    dialogRef.current?.showModal();
    async function fetchAccountAge() {
      try {
        const supabase = getClient();
        if (!supabase) return;
        const { data } = await supabase.auth.getUser();
        if (data.user?.created_at) {
          const days = Math.floor((Date.now() - new Date(data.user.created_at).getTime()) / 86_400_000);
          setAccountAgeDays(days);
        }
      } catch {
        /* account age is best-effort; ignore fetch failures */
      }
    }
    void fetchAccountAge();
  }, []);

  const canSubmit = [loves, hates, wishes].some((v) => v.trim().length > 0);

  function buildContext(): FeedbackContext {
    const cards = Object.values(useCardsStore.getState().cards);
    const prefs = usePrefsStore.getState();
    return {
      masteryPreset: prefs.masteryPreset,
      activePlanTemplateId: prefs.activeSavedPlanId,
      totalReviewed: cards.filter((c) => c.reps > 0).length,
      masteredCount: cards.filter((c) => c.state === "review").length,
      accountAgeDays,
      activeSkill,
      sessionAccuracy,
    };
  }

  async function handleSubmit() {
    if (!canSubmit || status === "submitting") return;
    setStatus("submitting");
    try {
      await submitFeedback({
        screen,
        loves,
        hates,
        wishes,
        platform: "web",
        wantsFollowup,
        context: buildContext(),
      });
      setStatus("done");
      setTimeout(onClose, 1500);
    } catch {
      setStatus("error");
    }
  }

  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === dialogRef.current) onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      onCancel={onClose}
      className="m-auto w-full max-w-lg bg-paper border border-ink/20 p-0 backdrop:bg-ink/40 open:flex open:flex-col"
      aria-label="Send feedback"
    >
      <div className="flex items-baseline justify-between px-6 pt-6 pb-4 border-b border-ink/10">
        <h2 className="font-serif italic text-xl text-ink">Leave feedback</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="font-mono text-sm text-inkFaded hover:text-ink transition-colors"
        >
          ✕
        </button>
      </div>

      {status === "done" ? (
        <div className="px-6 py-10 text-center">
          <p className="font-serif italic text-lg text-ink">Thanks — noted.</p>
        </div>
      ) : (
        <div className="px-6 py-5 flex flex-col gap-4">
          <Field label="What do you love?">
            <textarea
              value={loves}
              onChange={(e) => setLoves(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-ink/20 bg-paper text-sm text-ink resize-none focus:outline-none focus:border-accent"
              placeholder="Something that's working well…"
            />
          </Field>
          <Field label="What needs work?">
            <textarea
              value={hates}
              onChange={(e) => setHates(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-ink/20 bg-paper text-sm text-ink resize-none focus:outline-none focus:border-accent"
              placeholder="Something frustrating or broken…"
            />
          </Field>
          <Field label="What do you wish for?">
            <textarea
              value={wishes}
              onChange={(e) => setWishes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-ink/20 bg-paper text-sm text-ink resize-none focus:outline-none focus:border-accent"
              placeholder="A feature or change you'd love to see…"
            />
          </Field>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={wantsFollowup}
              onChange={(e) => setWantsFollowup(e.target.checked)}
              className="accent-accent w-4 h-4"
            />
            <span className="text-sm text-inkSoft">I&apos;d like a follow-up on this</span>
          </label>

          {status === "error" && (
            <p className="text-xs text-danger font-mono">Something went wrong — try again.</p>
          )}

          <div className="flex gap-3 pt-1 pb-1">
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit || status === "submitting"}
              className="flex-1"
            >
              {status === "submitting" ? "Sending…" : "Send feedback"}
            </Button>
            <Button variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-mono text-inkFaded uppercase tracking-widest">{label}</span>
      {children}
    </div>
  );
}
